import { fetch as fetchRaw } from '@lib/fetch';
import { getDefinedUrlSearchParams } from '@lib/util';
import { backend } from '../services';

// Base for the new-structure API (mounted under `/api`). Used by endpoints that
// have been migrated off the legacy audits-service.
const fetchBackend = (url, ...args) => fetchRaw([backend.url, url], ...args);

// Migrated to the new-structure endpoint GET /api/audits. The legacy
// { data, meta } envelope is preserved there, so callers are unchanged.
// (`backend.url` already contains `/api`.)
export function auditsGet(queryParams) {
  return fetchBackend(`/audits?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

// Byte-identical twin of `auditsGet`, kept because ~15 call sites import it
// under this name. Same migrated endpoint.
export function auditsNetworkGet(queryParams) {
  return fetchBackend(`/audits?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

// Migrated to the new-structure endpoint POST /api/audit-findings. That
// endpoint wraps its response as { metadata, data }, so we unwrap `data` back
// to the shape the caller expects (`response.data` === the created finding).
// The legacy audits-service endpoint (POST /v1/audit-findings) no longer
// exists.
export async function auditFindingCreate(body) {
  const res = await fetchBackend('/audit-findings', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return { ...res, data: res.data?.data ?? null };
}

// Migrated to the new-structure endpoint PATCH /api/audit-findings/:id. That
// endpoint wraps its response as { metadata, data }, so we unwrap `data` back
// to the shape the caller expects (`response.data` === the updated finding).
// The legacy audits-service endpoint (PATCH /v1/audit-findings/:id) no longer
// exists.
export async function auditFindingPatch(id, body) {
  const res = await fetchBackend(`/audit-findings/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  return { ...res, data: res.data?.data ?? null };
}

// Migrated to the new-structure endpoint PATCH /api/audit-findings/bulk-remove
// (204, no body; the callers only check the status). The legacy audits-service
// endpoint (DELETE /v1/audit-findings/bulk-remove) no longer exists.
export function auditFindingBulkRemove(body) {
  return fetchBackend('/audit-findings/bulk-remove', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

// Migrated to the new-structure endpoint GET /api/audits/:id. That endpoint
// wraps its response as { metadata, data }, so we unwrap `data` back to the
// shape existing consumers already expect (`response.data` === the audit),
// keeping the migration transparent for every caller. The legacy
// audits-service endpoint (GET /v1/audits/:id) no longer exists.
export async function auditGet(id) {
  const res = await fetchBackend(`/audits/${id}`, {
    method: 'GET',
  });
  return { ...res, data: res.data?.data ?? null };
}

// Migrated to the new-structure endpoint GET /api/admin/audits. The legacy
// { data, meta } envelope is preserved there, so callers are unchanged. The
// legacy audits-service endpoint (GET /v1/admin/audits) no longer exists.
export function auditsGetAdmin(queryParams) {
  return fetchBackend(
    `/admin/audits?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

// Migrated to the new-structure endpoint POST /api/audits. That endpoint wraps
// its response as { metadata, data }, so we unwrap `data` back to the shape the
// existing consumers already expect (`response.data` === the created audit),
// keeping the migration transparent for every caller. The legacy audits-service
// endpoint (POST /v1/audits) stays available for any non-frontend consumers.
export async function auditCreate(body) {
  const res = await fetchBackend('/audits', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return { ...res, data: res.data?.data ?? null };
}

// Migrated to the new-structure endpoint GET /api/audits/:id/versions. The
// legacy { data, meta } envelope is preserved there, so callers are unchanged.
// The legacy audits-service endpoint (GET /v1/audits/:id/versions) no longer
// exists.
export function auditVersionsGet(auditId, queryParams) {
  return fetchBackend(
    `/audits/${auditId}/versions?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

// Migrated to the new-structure endpoint PATCH /api/audits/:id. That endpoint
// wraps its response as { metadata, data }, so we unwrap `data` back to the
// shape existing consumers already expect (`response.data` === the audit).
// The legacy audits-service endpoint (PATCH /v1/audits/:id) no longer exists.
export async function auditsPatch(id, body) {
  const res = await fetchBackend(`/audits/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  return { ...res, data: res.data?.data ?? null };
}

// Migrated to the new-structure endpoint PUT /api/audits/:id. That endpoint
// wraps its response as { metadata, data }, so we unwrap `data` back to the
// shape existing consumers already expect (`response.data` === the audit).
// The legacy audits-service endpoint (PUT /v1/audits/:id) no longer exists.
export async function auditsUpdate(id, body) {
  const res = await fetchBackend(`/audits/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return { ...res, data: res.data?.data ?? null };
}

// Migrated to the new-structure endpoint DELETE /api/admin/audit-types/:id
// (204, no body; the caller only checks the status). The legacy audits-service
// endpoint (DELETE /v1/admin/audit-types/:id) no longer exists.
export function deleteAuditType({ id }) {
  return fetchBackend(`/admin/audit-types/${id}`, {
    method: 'DELETE',
  });
}
// Migrated to the new-structure endpoint PATCH /api/admin/audit-types/:id/usage
// (the caller only checks the status, then refetches the list). The legacy
// audits-service endpoint (PATCH /v1/admin/audit-types/:id/usage) no longer
// exists.
export function updateAuditTypeUsage({ id, deprecate }) {
  return fetchBackend(`/admin/audit-types/${id}/usage`, {
    method: 'PATCH',
    body: JSON.stringify({ deprecate }),
  });
}

// Migrated to the new-structure endpoint POST /api/admin/audit-types. That
// endpoint wraps its response as { metadata, data }, so we unwrap `data` back
// to the shape the caller expects (`response.data` === the created type). The
// legacy audits-service endpoint (POST /v1/admin/audit-types) no longer exists.
export async function createAuditType(body) {
  const res = await fetchBackend('/admin/audit-types', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return { ...res, data: res.data?.data ?? null };
}

// Migrated to the new-structure endpoint PATCH /api/admin/audit-types/:id.
// That endpoint wraps its response as { metadata, data }, so we unwrap `data`
// back to the shape the caller expects (`response.data` === the updated type,
// which replaces the table row). The legacy audits-service endpoint
// (PATCH /v1/admin/audit-types/:id) no longer exists.
export async function patchAuditType(id, body) {
  const res = await fetchBackend(`/admin/audit-types/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  return { ...res, data: res.data?.data ?? null };
}

// Migrated to the new-structure endpoint GET /api/audits/:id/access-requests.
// The legacy { data, meta } envelope is preserved there, so callers are
// unchanged. The legacy audits-service endpoint
// (GET /v1/audit-access-requests/:id) no longer exists.
export function auditRequestsGet(id, queryParams) {
  return fetchBackend(
    `/audits/${id}/access-requests?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

// Migrated to the new-structure endpoint PATCH /api/audits/bulk-archive. Its
// callers ignore the response body. The legacy audits-service endpoint
// (PATCH /v1/audits/bulk-archive) no longer exists.
export function auditBulkArchive(body) {
  return fetchBackend('/audits/bulk-archive', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

// Migrated to the new-structure endpoint PATCH /api/audits/bulk-unarchive. Its
// callers ignore the response body. The legacy audits-service endpoint
// (PATCH /v1/audits/bulk-unarchive) no longer exists.
export function auditBulkUnarchive(body) {
  return fetchBackend('/audits/bulk-unarchive', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

// Migrated to the new-structure endpoint POST /api/audits/:id/request. Callers
// only check the status. The legacy audits-service endpoint
// (POST /v1/audits/:id/request) no longer exists.
export function auditRequest(id) {
  return fetchBackend(`/audits/${id}/request`, {
    method: 'POST',
  });
}

/**
 * Cancels the caller's own open access request.
 *
 * Pairs with `auditRequest` above: both must work on
 * `audits_service_audit_access_requests`, where the request flow records it.
 * The legacy `POST /v3/audits/:id/cancel-request` this once replaced deleted an
 * `audit_permissions` row, and nothing has written that table since 2024-10-22 —
 * so it cancelled nothing [DELTA-2545]. Migrated to the new-structure endpoint
 * `POST /api/audits/:id/cancel-request`, which writes that same table; callers
 * ignore the response body. The legacy audits-service endpoint
 * (POST /v1/audits/:id/cancel-request) no longer exists.
 */
export function auditCancelRequest(id) {
  return fetchBackend(`/audits/${id}/cancel-request`, {
    method: 'POST',
  });
}

/**
 * Makes an audit public over `PATCH /api/audits/:id`, replacing
 * `PATCH /v3/audits/make-public`.
 *
 * Public is a read-time rule now — visible to every company connected to the
 * audited one — so this writes the flag and nothing else. The v3 route also
 * deleted that audit's pending `audit_permissions` rows; that table is being
 * retired and has no pending rows on any public audit anyway [DELTA-2545].
 */
export function auditMakePublic(id) {
  return auditsPatch(id, { isPublic: true });
}

// Replaces the legacy audits-service list (GET /v1/slcp-assessments), whose one
// caller — the company-profile SLCP banner — read only row 0. The new-structure
// endpoint GET /api/slcp/assessments/own/latest returns that assessment (with
// its `history`) or null, wrapped as { metadata, data }; we unwrap `data` so
// `response.data` is the assessment. The legacy endpoint no longer exists.
export async function getLatestOwnAssessment() {
  const res = await fetchBackend('/slcp/assessments/own/latest', {
    method: 'GET',
  });
  return { ...res, data: res.data?.data ?? null };
}

// Migrated to the new-structure endpoint POST /api/slcp/assessments/fetch
// (202; queues the gateway fetch — the caller only refetches the list after a
// countdown). The legacy audits-service endpoint
// (POST /v1/slcp-assessments/fetch-assessments) no longer exists.
export function fetchAssessments() {
  return fetchBackend('/slcp/assessments/fetch', {
    method: 'POST',
  });
}

// Migrated to the new-structure endpoint GET /api/slcp/assessments/:id/attachment.
// It takes the assessment id (the legacy route took the SLCP object id) and
// wraps its response as { metadata, data }, so we unwrap `data` back to the
// shape the caller expects (`response.data.url`). Error codes are unchanged.
// The legacy audits-service endpoint
// (GET /v1/slcp-assessments/:objectId/attachment) no longer exists.
export async function getAssessmentAttachmentLink(id) {
  const res = await fetchBackend(`/slcp/assessments/${id}/attachment`, {
    method: 'GET',
  });
  return { ...res, data: res.data?.data ?? null };
}

// Migrated to the new-structure endpoint GET /api/slcp/assessments/:id/document
// (the Gateway document; `GET /api/slcp/assessments/:id` is the record). That
// endpoint wraps its response as { metadata, data }, so we unwrap `data` back
// to the shape the callers expect (`response.data` === the assessment). The
// legacy audits-service endpoint (GET /v1/slcp-assessments/:id) no longer
// exists.
export async function getAssessmentById(id) {
  const res = await fetchBackend(`/slcp/assessments/${id}/document`, {
    method: 'GET',
  });
  return { ...res, data: res.data?.data ?? null };
}

// Migrated to the new-structure endpoint POST /api/slcp/assessments/:id/history.
// The companies are derived server-side now (by/on behalf of = the caller, for
// = the facility's company), so only the failed data points are sent. The
// caller ignores the response. The legacy audits-service endpoint
// (POST /v1/slcp-assessments/history) no longer exists.
export function createSlcpAssessmentHistory({
  slcpAssessmentId,
  failedDatapoints,
}) {
  return fetchBackend(`/slcp/assessments/${slcpAssessmentId}/history`, {
    method: 'POST',
    body: JSON.stringify({ failedDatapoints }),
  });
}

// Migrated to the new-structure endpoint GET /api/admin/slcp/analytics. That
// endpoint wraps its response as { metadata, data }, so we unwrap `data` back
// to the shape the caller expects (`response.data` === the analytics). The
// legacy audits-service endpoint (GET /v1/admin/slcp/analytics) no longer
// exists.
export async function getAdminSlcpAnalytics(queryParams) {
  const res = await fetchBackend(
    `/admin/slcp/analytics?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
  return { ...res, data: res.data?.data ?? null };
}
